import React from 'react';
import { View, StyleSheet, Image, ViewStyle, TouchableOpacity, Text } from 'react-native';

import { getLocalizedTexts } from '../../Locales';
import { Browser, browserKind, BrowserExtensionStoreUrl } from '../../lib/common/browser';
import { MarketingWebsitePath, getMarketingWebsiteUrl, getRegion, Region } from '../../lib/auth/region';
import { containerStyles } from '../../style/container';
import { font } from '../../style/text';
import color from '../../style/color';

const jokoLogo = { uri: '/assets/images/logos/joko-title-gray.svg' };
const firefoxLogo = { uri: '/assets/images/logos/browsers/firefox.svg' };
const safariLogo = { uri: '/assets/images/logos/browsers/safari.svg' };
const chromeLogo = { uri: '/assets/images/logos/browsers/chrome.svg' };

type BrowserExtensionStoreInfo = {
    logo: { uri: string };
    link: string;
    name: string;
};

const browserExtensionStoreInfo: BrowserExtensionStoreInfo =
    browserKind === Browser.safari
        ? { logo: safariLogo, link: BrowserExtensionStoreUrl.safari, name: 'Safari app store' }
        : browserKind === Browser.firefox
        ? { logo: firefoxLogo, link: BrowserExtensionStoreUrl.firefox, name: 'Firefox add-ons' }
        : { logo: chromeLogo, link: BrowserExtensionStoreUrl.chrome, name: 'Chrome web store' };

function Footer({ style }: { style?: ViewStyle }) {
    const texts = getLocalizedTexts().home.footer;
    // The Legal Notice is a French (LCEN) requirement - France only for now, per Legal (Maya).
    const isFrance = getRegion() === Region.FR;
    return (
        <View style={[containerStyles.containerCenter, style]}>
            <Image source={jokoLogo} style={styles.imageJokoLogo} />
            <TouchableOpacity
                style={[styles.containerBrowserExtensionStoreLink, !isFrance && styles.containerBrowserExtensionStoreLinkStandalone]}
                onPress={() => window.open(browserExtensionStoreInfo.link)}>
                <Image source={browserExtensionStoreInfo.logo} style={styles.imageBrowserExtensionLogo} />
                <Text style={styles.textBrowserExtensionStoreName}>{browserExtensionStoreInfo.name}</Text>
            </TouchableOpacity>
            {isFrance ? (
                <>
                    <View style={styles.containerLegalLinks}>
                        <FooterLegalLink
                            url={getMarketingWebsiteUrl({ path: MarketingWebsitePath.legalNotice })}
                            text={texts.legalNotice}
                        />
                        <Text style={styles.textLegalLink}> · </Text>
                        <FooterLegalLink
                            url={getMarketingWebsiteUrl({ path: MarketingWebsitePath.termOfService })}
                            text={texts.termsOfService}
                        />
                        <Text style={styles.textLegalLink}> · </Text>
                        <FooterLegalLink
                            url={getMarketingWebsiteUrl({ path: MarketingWebsitePath.privacyPolicy })}
                            text={texts.privacyPolicy}
                        />
                    </View>
                    <Text style={styles.textCopyright}>{texts.copyright({ year: new Date().getFullYear() })}</Text>
                </>
            ) : null}
        </View>
    );
}

export default Footer;

function FooterLegalLink({ url, text }: { url: string; text: string }) {
    return (
        <TouchableOpacity onPress={() => window.open(url)}>
            <Text style={styles.textLegalLink}>{text}</Text>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    containerBrowserExtensionStoreLink: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    containerBrowserExtensionStoreLinkStandalone: {
        marginBottom: 60,
    },
    containerLegalLinks: {
        flexDirection: 'row',
    },
    imageJokoLogo: {
        height: 28.5,
        width: 85,
        marginTop: 40,
        marginBottom: 20,
    },
    imageBrowserExtensionLogo: {
        height: 24,
        width: 24,
        marginRight: 8,
    },
    textBrowserExtensionStoreName: {
        fontSize: 14,
        fontFamily: font.ambitBold,
        color: color.frenchGray,
    },
    textLegalLink: {
        fontSize: 13,
        fontFamily: font.ambitSemiBold,
        color: color.silverChalice,
    },
    textCopyright: {
        fontSize: 13,
        fontFamily: font.ambitSemiBold,
        color: color.silverChalice,
        marginTop: 4,
        marginBottom: 60,
    },
});
